import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppService, EntityStatus, NavigationService, SocketEventNamespace } from '@core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ClientManagementActions, SocketEvents } from '@ngrx-store';
import { NotificationsService } from '@ui';
import { forkJoin, map, of, switchMap, take } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';

import { locationUtils } from '../helpers';
import { type FloorSpaceModel, type LocationData, type LocationFullData, type RoomSpaceModel, SpaceType } from '../models';
import { LocationService } from '../services/location.service';
import { LocationActions } from './locations.actions';

@Injectable()
export class LocationsEffects {
  private actions$ = inject(Actions);
  private locationService = inject(LocationService);
  private notificationService = inject(NotificationsService);
  private appService = inject(AppService);
  private navigationService = inject(NavigationService);
  private store = inject(Store);
  private router = inject(Router);

  constructor() {
    SocketEvents.pipe(
      filter(({ type }) => type.startsWith(SocketEventNamespace.INCIDENTS)),
      map(({ data }) => Array.isArray(data) ? data : [data]),
      takeUntilDestroyed(),
    ).subscribe((data) => {
      data.forEach(item => {
        // TODO : Need refactoring : different data : Naberezhniev
        const locId = this.navigationService.route.snapshot.params['locationId'];
        const componentName = this.navigationService.route.component?.name;
        const locationId = item?.device?.locationId || item?.data?.[0]?.device?.locationId || '';

        if (locationId === locId) {
          this.store.dispatch(LocationActions.getLocationState({
            excludeArchived: true,
            locationId,
          }));
        } else if (!locId && componentName === 'DashboardMainComponent') {
          this.store.dispatch(LocationActions.loadLocations({ clientId: this.appService.currentClient }));
        }
      });
    });
  }

  clientIdChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientManagementActions.clientIdChanged),
      switchMap(({ clientId }) => [
        LocationActions.resetDefaultLocationsState(),
        LocationActions.loadLocations({ clientId }),
      ]),
    );
  });

  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.loadLocations),
      switchMap(() =>
        this.locationService.getLocationsList().pipe(
          map(locations => LocationActions.locationsLoaded({ location: locations })),
          catchError(error => of(LocationActions.locationError({ locationError: error }))),
        ),
      ),
    ),
  );

  loadLocationsSummaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.locationsLoaded),
      switchMap(() =>
        this.locationService
          .getLocationsSummaries(this.appService.currentClient, { deviceStatus: ['active', 'paused'] })
          .pipe(
            map(locationsSummaries => {
              const locationsUpdates = locationsSummaries.map(({ id, summary }) => ({ changes: summary, id }));

              return LocationActions.loadLocationsSummariesSuccess({ data: locationsUpdates });
            }),
            catchError(() => of(LocationActions.loadLocationsSummariesError())),
          ),
      ),
    ),
  );

  createLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.addLocation),
      switchMap(action =>
        this.locationService.addLocation(action.clientId, action.newLocation).pipe(
          map(location => LocationActions.addLocationSuccess({ newLocation: location })),
          catchError(error => of(LocationActions.locationError({ locationError: error }))),
        ),
      ),
    );
  });

  locationCreated$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.addLocationSuccess),
      tap(location => {
        this.notificationService.showSuccessMessage(
          'The new location - ' + location.newLocation.friendlyName + ' was created',
        );
      }),
      take(1),
    );
  });

  updateLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.updateLocation),
      switchMap(action => {
        return this.locationService.updateLocation(action.clientId, action.locationId, action.data).pipe(
          switchMap(updatedLocation =>
            this.locationService
              .getLocationsSummaries(action.clientId, {
                deviceStatus: ['active', 'paused'],
                locationId: action.locationId,
              })
              .pipe(
                map(locationsSummary => {
                  const summary = locationsSummary.find(summary => summary.id === updatedLocation.id)?.summary || {};


                  return { ...updatedLocation, ...summary };
                }),
                map(locationFullData => {
                  this.notificationService.showSuccessMessage(
                    'The location - ' + locationFullData.friendlyName + ' was updated',
                  );

                  return LocationActions.updateLocationSuccess({ location: locationFullData });
                }),
              ),
          ),
          catchError(error => of(LocationActions.locationError({ locationError: error }))),
        );
      }),
    );
  });


  getLocationState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.getLocationState),
      switchMap(action => {
        const floorOptions: Record<string, string> = {
          includeArchived: 'true',
          includeNestedDevices: 'true',
          type: SpaceType.floor,
        };
        const roomOptions: Record<string, string> = { includeArchived: 'true', type: SpaceType.room };

        return forkJoin([
          this.locationService.getLocationInfoById(action.locationId),
          this.locationService.getSpacesList(action.locationId, floorOptions),
          this.locationService.getSpacesList(action.locationId, roomOptions),
          this.locationService.getSpacesSummaries(this.appService.currentClient, action.locationId, {
            deviceStatus: ['active', 'paused'],
            includeChildSpaceDevices: true,
            type: 'Floor',
          }),
          this.locationService.getSpacesSummaries(this.appService.currentClient, action.locationId, {
            deviceStatus: ['active', 'paused'],
            type: 'Room',
          }),
        ]).pipe(
          map(([location, floors, rooms, floorSummaries, roomSummaries]) => {
            const fullDataFloors = floors.map(floor => {
              const floorSummary = floorSummaries.find(summary => floor.id === summary.id);


              return {
                ...floor,
                ...(floorSummary ? floorSummary.summary : {}),
              };
            }).filter(locationUtils.isFloor);

            const fullDataRooms = rooms.map(room => {
              const roomSummary = roomSummaries.find(summary => room.id === summary.id);


              return {
                ...room,
                ...(roomSummary ? roomSummary.summary : {}),
              };
            }).filter(locationUtils.isRoom);

            const sortedRooms = this.sortRoomsSpacesByParentId(fullDataRooms, action.excludeArchived);
            let locationData = this.buildLocationData(location, fullDataFloors, action.excludeArchived);

            locationData = this.assignRoomsToFloors(locationData, sortedRooms);

            return LocationActions.getLocationStateSuccess({ locationState: locationData });
          }),
          catchError(error => of(LocationActions.locationError({ locationError: error }))),
        );
      }),
    ),
  );

  locationStateLoaded$ = createEffect(() => {
    return this.actions$.pipe(ofType(LocationActions.getLocationStateSuccess), take(1));
  });

  locationErrorHandler = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LocationActions.locationError),
        tap(error => {
          this.notificationService.showErrorMessage(error.locationError.message);
        }),
      ),
    { dispatch: false },
  );

  deleteLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.deleteLocation),
      switchMap(action =>
        this.locationService.deleteLocation(action.locationId).pipe(
          map(() => LocationActions.dropLocation({ locationId: action.locationId })),
          tap(async () => {
            this.notificationService.showSuccessMessage('Location removed successfully.');
            if (action.navigateToParent) {
              await this.router.navigate(['/dashboard']);
            }
          }),
          catchError((error) => {
            this.notificationService.showErrorMessage('An unexpected error occurred. Please try again later.');

            return of(LocationActions.deleteLocationError({ error }));
          }),
        ),
      ),
    ),
  );

  // Floor effects
  createFloor$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.addFloor),
      switchMap(action => {
        return this.locationService.addNewSpace<FloorSpaceModel>(this.appService.currentClient, action.locationId, action.data).pipe(
          map(floor => {
            this.notificationService.showSuccessMessage(`Floor [${floor.friendlyName}] was successfully created`);

            return LocationActions.addFloorSuccess({ floor });
          }),
          catchError(error => {
            this.notificationService.showErrorMessage(error.message);

            return of(LocationActions.addFloorError({ error }));
          }),
        );
      }),
    );
  });

  updateFloor$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.updateFloor),
      switchMap(action => {
        return this.locationService
          .updateSpace<FloorSpaceModel>(this.appService.currentClient, action.locationId, action.spaceId, action.data)
          .pipe(
            switchMap(updatedFloor =>
              this.locationService
                .getSpacesSummaries(this.appService.currentClient, action.locationId, {
                  deviceStatus: ['active', 'paused'],
                  includeChildSpaceDevices: true,
                  spaceId: action.spaceId,
                })
                .pipe(
                  map(floorsSummary => ({ ...updatedFloor, ...floorsSummary[0].summary } as FloorSpaceModel)),
                  map(floorFullData => {
                    this.notificationService.showSuccessMessage(
                      `Floor [${floorFullData.friendlyName}] was successfully updated`,
                    );

                    return LocationActions.updateFloorSuccess({ floor: floorFullData, locationId: action.locationId });
                  }),
                ),
            ),
            catchError(error => {
              this.notificationService.showErrorMessage(error.message);

              return of(LocationActions.updateFloorError({ error }));
            }),
          );
      }),
    );
  });

  deleteFloor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.deleteFloor),
      switchMap(action =>
        this.locationService.deleteSpace(action.locationId, action.floorId).pipe(
          map(() => LocationActions.dropFloor({ floorId: action.floorId })),
          tap(async () => {
            this.notificationService.showSuccessMessage('Floor removed successfully.');
            if (action.navigateToParent) {
              await this.router.navigate(['/dashboard', action.locationId]);
            }
          }),
          catchError((error) => {
            this.notificationService.showErrorMessage('An unexpected error occurred. Please try again later.');

            return of(LocationActions.deleteFloorError({ error }));
          }),
        ),
      ),
    ),
  );

  reorderFloors$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.reorderFloors),
      switchMap(action => {
        return this.locationService.bulkSpaceReorder(this.appService.currentClient, action.locationId, action.floors).pipe(
          map(() => {
            this.notificationService.showSuccessMessage('Floors reordered successfully');

            return LocationActions.reorderFloorsSuccess();
          }),
          catchError(error => {
            this.notificationService.showErrorMessage(error.message);

            return of(LocationActions.reorderFloorsError());
          }),
        );
      }),
    );
  });

  // Room effects
  createRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.addRoom),
      switchMap(action => {
        return this.locationService.addNewSpace<RoomSpaceModel>(this.appService.currentClient, action.locationId, action.data).pipe(
          map(room => {
            this.notificationService.showSuccessMessage(`Room [${room.friendlyName}] was successfully created`);

            return LocationActions.addRoomSuccess({ room });
          }),
          catchError(error => {
            this.notificationService.showErrorMessage(error.message);

            return of(LocationActions.addRoomError({ error }));
          }),
        );
      }),
    );
  });

  updateRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.updateRoom),
      switchMap(action => {
        return this.locationService
          .updateSpace<RoomSpaceModel>(this.appService.currentClient, action.locationId, action.spaceId, action.data)
          .pipe(
            switchMap(updatedRoom =>
              this.locationService
                .getSpacesSummaries(this.appService.currentClient, action.locationId, {
                  deviceStatus: ['active', 'paused'],
                  spaceId: action.spaceId,
                })
                .pipe(
                  map(roomsSummary => ({ ...updatedRoom, ...roomsSummary[0].summary } as RoomSpaceModel)),
                  map(roomFullData => {
                    this.notificationService.showSuccessMessage(
                      `Room [${roomFullData.friendlyName}] was successfully updated`,
                    );

                    return LocationActions.updateRoomSuccess({ room: roomFullData });
                  }),
                ),
            ),
            catchError(error => {
              this.notificationService.showErrorMessage(error.message);

              return of(LocationActions.updateRoomError({ error }));
            }),
          );
      }),
    );
  });

  uploadPlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.uploadPlan),
      switchMap(action => {
        return this.locationService
          .uploadDocumentToSpace({
            body: action.file,
            clientId: this.appService.currentClient,
            locationId: action.locationId,
            spaceId: action.roomId || action.floorId,
            tab: action.tab,
          })
          .pipe(
            map(document => {
              this.notificationService.showSuccessMessage('Roomplan was successfully uploaded');

              return LocationActions.uploadPlanSuccess({ document, floorId: action.floorId, roomId: action.roomId });
            }),
            catchError(error => {
              this.notificationService.showErrorMessage(error.message);

              return of(LocationActions.uploadPlanError({ error }));
            }),
          );
      }),
    );
  });

  deletePlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.deleteRoomplan),
      switchMap(action => {
        return this.locationService
          .deleteDocumentFromSpace({
            clientId: this.appService.currentClient,
            filename: action.fileName,
            locationId: action.locationId,
            spaceId: action.roomId,
          })
          .pipe(
            map(() => {
              this.notificationService.showSuccessMessage('Roomplan was successfully deleted');

              return LocationActions.deleteRoomplanSuccess({
                fileName: action.fileName,
                floorId: action.floorId,
                roomId: action.roomId,
              });
            }),
            catchError(error => {
              this.notificationService.showErrorMessage(error.message);

              return of(LocationActions.deleteRoomplanError({ error }));
            }),
          );
      }),
    );
  });

  getSpaceDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.getSpaceDocuments),
      switchMap(action => {
        return this.locationService
          .getAllSpaceDocuments({
            clientId: this.appService.currentClient,
            locationId: action.locationId,
            spaceId: action.roomId || action.floorId,
          })
          .pipe(
            map(results => results.sort((a, b) => new Date(a.uploadedOn).getTime() - new Date(b.uploadedOn).getTime())),
            map(documents => {
              return LocationActions.getSpaceDocumentsSuccess({ documents, floorId: action.floorId, roomId: action.roomId });
            }),
          );
      }),
    );
  });

  deleteRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationActions.deleteRoom),
      switchMap(action =>
        this.locationService.deleteSpace(action.locationId, action.roomId).pipe(
          map(() => LocationActions.dropRoom({ floorId: action.floorId, roomId: action.roomId })),
          tap(async () => {
            this.notificationService.showSuccessMessage('Room removed successfully.');
            if (action.navigateToParent) {
              await this.router.navigate(['/dashboard', action.locationId, action.floorId]);
            }
          }),
          catchError((error) => {
            this.notificationService.showErrorMessage('An unexpected error occurred. Please try again later.');

            return of(LocationActions.deleteRoomError({ error }));
          }),
        ),
      ),
    ),
  );

  // Floorplan effects
  uploadFloorplan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.uploadFloorplan),
      switchMap(action => {
        return this.locationService
          .uploadDocumentToSpace({
            body: action.file,
            clientId: this.appService.currentClient,
            locationId: action.locationId,
            spaceId: action.spaceId,
            tab: action.tab,
          })
          .pipe(
            map(document => {
              this.notificationService.showSuccessMessage('Floorplan was successfully uploaded');

              return LocationActions.uploadFloorplanSuccess({ document, spaceId: action.spaceId });
            }),
            catchError(error => {
              this.notificationService.showErrorMessage(error.message);

              return of(LocationActions.uploadFloorplanError({ error }));
            }),
          );
      }),
    );
  });

  deleteFloorplan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationActions.deleteFloorplan),
      switchMap(action => {
        return this.locationService
          .deleteDocumentFromSpace({
            clientId: this.appService.currentClient,
            filename: action.fileName,
            locationId: action.locationId,
            spaceId: action.spaceId,
          })
          .pipe(
            map(() => {
              this.notificationService.showSuccessMessage('Floorplan was successfully deleted');

              return LocationActions.deleteFloorplanSuccess({ spaceId: action.spaceId });
            }),
            catchError(error => {
              this.notificationService.showErrorMessage(error.message);

              return of(LocationActions.deleteFloorplanError({ error }));
            }),
          );
      }),
    );
  });


  private buildLocationData(
    location: LocationData,
    floors: FloorSpaceModel[],
    excludeArchived: boolean,
  ): LocationFullData {
    const locationData: LocationFullData = { ...location, floors: [] };

    locationData.floors = floors;
    if (excludeArchived) {
      locationData.floors = locationData.floors.filter(item => item.status !== EntityStatus.Archived);
    }

    return locationData;
  }

  private assignRoomsToFloors(
    locationData: LocationFullData,
    sortedRooms: {
      [key: string]: RoomSpaceModel[];
    },
  ): LocationFullData {
    if (locationData.floors.length) {
      locationData.floors.forEach((floor: FloorSpaceModel) => {
        floor.rooms = floor.id ? sortedRooms[floor.id] : [];
      });
    }

    return locationData;
  }

  private sortRoomsSpacesByParentId(
    sortingCollection: RoomSpaceModel[],
    excludeArchived = false,
  ): {
    [key: string]: RoomSpaceModel[];
  } {
    const res: { [key: string]: RoomSpaceModel[]; } = {};

    sortingCollection.forEach(item => {
      if (excludeArchived && item.status === EntityStatus.Archived) {
        return;
      }
      const parentId = item.parentSpaceId;

      if (parentId) {
        if (!res[parentId]) {
          res[parentId] = [];
        }
        res[parentId].push(item);
      }
    });

    return res;
  }
}
